import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Route as RouteIcon, Navigation, Trash2 } from "lucide-react";
import { api, errMsg, todayRome } from "@/lib/api";
import { fmtDuration, gmapsLinks } from "@/lib/route";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, Field, NativeSelect, PageHead, StatCard } from "@/components/common";
import { RiconsegneList, RitiriList, ExtraStops } from "@/components/route/StopPicker";
import { RouteMap } from "@/components/route/RouteMap";
import { RouteTimeline } from "@/components/route/RouteTimeline";

const DEPOT = "Via Leonardo Greppi 12, 00149 Roma";
const addr = (a, c) => [a, c].filter((x) => x && x.trim()).join(", ");
const Section = ({ title, children }) => (
  <div className="space-y-3">
    <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#B8860B]">{title}</p>
    {children}
  </div>
);

export default function Riconsegne() {
  const [date, setDate] = useState(todayRome());
  const [opts, setOpts] = useState({ start_time: "08:30", stop_minutes: 15, roundtrip: true });
  const [pending, setPending] = useState([]);
  const [ritiri, setRitiri] = useState([]);
  const [selR, setSelR] = useState({});
  const [selB, setSelB] = useState({});
  const [extra, setExtra] = useState([]);
  const [route, setRoute] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPending = useCallback(() => {
    api.get("/admin/riconsegne").then((r) => setPending(r.data.filter((p) => !p.riconsegnato)));
  }, []);
  useEffect(loadPending, [loadPending]);
  useEffect(() => {
    api.get("/admin/bookings", { params: { date_from: date, date_to: date } }).then((r) => {
      const active = r.data.filter((b) => !["annullato", "completato"].includes(b.status));
      setRitiri(active);
      setSelB(Object.fromEntries(active.map((b) => [b.id, true])));
    });
    api.get("/admin/percorso", { params: { date } }).then((r) => setRoute(r.data));
  }, [date]);

  const unflag = async (p) => {
    await api.patch(`/admin/pratiche/${p.id}/riconsegna`, { da_riconsegnare: false });
    setSelR((s) => { const n = { ...s }; delete n[p.id]; return n; });
    loadPending();
  };

  const stops = () => [
    ...pending.filter((p) => selR[p.id]).map((p) => ({
      kind: "riconsegna", ref_id: p.id, label: `${p.nome} – ${p.animale_nome || p.specie || "animale"}`,
      address: addr(p.indirizzo, p.citta), phone: p.telefono || "",
      window_from: selR[p.id].from || null, window_to: selR[p.id].to || null,
      note: p.residuo > 0 ? `Da incassare € ${p.residuo.toFixed(2)}` : "",
    })),
    ...ritiri.filter((b) => selB[b.id]).map((b) => ({
      kind: "ritiro", ref_id: b.id, label: `Ritiro ${b.vet_name || b.owner_name || ""} – ${b.pet_name || b.species || ""}`,
      address: addr(b.pickup_address, b.city), phone: b.owner_phone || "",
      window_from: b.slot_start != null ? `${String(b.slot_start).padStart(2, "0")}:00` : null, window_to: b.slot_start != null ? `${String(b.slot_start + 1).padStart(2, "0")}:00` : null,
    })),
    ...extra.map((x) => ({ kind: "extra", label: x.label || "Tappa extra", address: x.address })),
  ];

  const calcola = async () => {
    const list = stops();
    if (!list.length) return toast.error("Seleziona almeno una tappa");
    setBusy(true);
    try {
      const r = await api.post("/admin/percorso/calcola", { date, ...opts, stop_minutes: Number(opts.stop_minutes), depot_address: DEPOT, stops: list });
      setRoute(r.data);
      toast.success("Percorso più veloce calcolato");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const onRouteChange = (r) => { setRoute(r); loadPending(); };
  const delRoute = async () => { if (window.confirm("Eliminare il percorso di questa giornata?")) { await api.delete(`/admin/percorso/${date}`); setRoute(null); } };
  const count = Object.keys(selR).length + Object.values(selB).filter(Boolean).length + extra.length;

  return (
    <div data-testid="admin-riconsegne">
      <PageHead eyebrow="Logistica" title="Riconsegne e giro del giorno" subtitle="Seleziona riconsegne, ritiri e tappe extra: calcolo il percorso più veloce con gli orari di arrivo stimati. Durante il giro segna gli arrivi reali." />
      <div className="grid lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-5 p-5 sm:p-6 space-y-6 h-fit">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Giorno"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white" data-testid="route-date" /></Field>
            <Field label="Partenza"><Input type="time" value={opts.start_time} onChange={(e) => setOpts({ ...opts, start_time: e.target.value })} className="bg-white" data-testid="route-start-time" /></Field>
            <Field label="Sosta per tappa">
              <NativeSelect value={opts.stop_minutes} onChange={(e) => setOpts({ ...opts, stop_minutes: e.target.value })} data-testid="route-stop-minutes">
                {[10, 15, 20, 30].map((m) => <option key={m} value={m}>{m} minuti</option>)}
              </NativeSelect>
            </Field>
            <label className="flex items-center gap-2 text-sm text-[#0D3B52] pt-6">
              <Checkbox checked={opts.roundtrip} onCheckedChange={(v) => setOpts({ ...opts, roundtrip: !!v })} data-testid="route-roundtrip" />Rientro in sede
            </label>
          </div>
          <p className="text-xs text-[#5C6B73] -mt-3">Partenza da {DEPOT}</p>
          <Section title={`Riconsegne da fare (${pending.length})`}><RiconsegneList items={pending} sel={selR} setSel={setSelR} onUnflag={unflag} /></Section>
          <Section title={`Ritiri del giorno (${ritiri.length})`}><RitiriList items={ritiri} sel={selB} setSel={setSelB} /></Section>
          <Section title="Tappe extra"><ExtraStops items={extra} setItems={setExtra} /></Section>
          <Button onClick={calcola} disabled={busy || !count} data-testid="route-calculate" className="w-full h-12 rounded-full bg-[#0D3B52] hover:bg-[#082535]">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Calcolo in corso…</> : <><RouteIcon className="h-4 w-4 mr-2" />Calcola percorso più veloce ({count} tappe)</>}
          </Button>
        </Card>

        <div className="lg:col-span-7 space-y-5">
          {!route ? (
            <Card className="p-10 text-center text-[#5C6B73]" data-testid="route-empty">Nessun percorso calcolato per questa giornata.</Card>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Tappe" value={route.stops.length} testid="route-stat-stops" />
                <StatCard label="Km totali" value={route.total_km} testid="route-stat-km" />
                <StatCard label="Guida" value={fmtDuration(route.total_drive_min)} testid="route-stat-drive" />
                <StatCard label={route.roundtrip ? "Rientro" : "Fine giro"} value={route.roundtrip ? route.return_eta : route.end_eta} tone="gold" testid="route-stat-end" />
              </div>
              {route.late_count > 0 && <p className="text-sm font-semibold text-rose-700">Attenzione: {route.late_count} tappe non rientrano nella fascia oraria richiesta.</p>}
              <div className="flex flex-wrap gap-2">
                {gmapsLinks(route).map((l, i, arr) => (
                  <a key={l} href={l} target="_blank" rel="noreferrer" data-testid={`route-gmaps-${i}`}
                    className="inline-flex items-center rounded-full bg-[#0D3B52] hover:bg-[#082535] text-white px-4 h-10 text-sm font-semibold">
                    <Navigation className="h-4 w-4 mr-2" />Naviga con Google Maps{arr.length > 1 ? ` (tratto ${i + 1})` : ""}
                  </a>
                ))}
                <Button variant="outline" onClick={delRoute} className="rounded-full" data-testid="route-delete"><Trash2 className="h-4 w-4 mr-1" />Elimina percorso</Button>
              </div>
              <RouteMap route={route} />
              <p className="text-xs text-[#5C6B73]">Orari stimati senza traffico in tempo reale (OpenStreetMap). Con "Naviga" Google Maps mostra l'arrivo col traffico reale.</p>
              <RouteTimeline route={route} onChange={onRouteChange} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
